import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import {
  CtWorkspaceDto,
  MeasurementDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { TabDetailPanelSettings } from '@common-modules/dependencies/navigation/tab-detail-component';
import {
  TabDetailPanelParameters,
  TabDetailParameterName,
} from '@common-modules/dependencies/navigation/tab-detail-panel-parameters';
import { DropdownNavigationItem } from '@common-modules/navigation/models/dropdown-navigation-item';
import { BasePageComponent } from '@common-modules/shared/component/base-page.component';
import { GridSetting } from '@common-modules/shared/constants/grid.constants';
import { GridSettingsService } from '@common-modules/shared/core/grid/grid-settings.service';
import { DataBindingFilters } from '@common-modules/shared/filters/component-filters/data-binding-filters';
import { GridBtnsEvent } from '@common-modules/shared/grid-buttons/models/grid-btns-event';
import { GridBtnsOptions } from '@common-modules/shared/grid-buttons/models/grid-btns-options.enum';
import { ReplaySubject, Subject } from 'rxjs';
import { MeasurePointConfigurationComponent } from '../measure-point-configuration/measure-point-configuration.component';
import { TelemetryFilters } from '../models/telemetry-filters';

const COMPONENT_SELECTOR = 'ct-telemetry-page';
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './telemetry-page.component.html',
  styleUrl: './telemetry-page.component.scss',
  standalone: false,
})
export class TelemetryPageComponent extends BasePageComponent implements OnInit {
  public get titleTranslationKey(): string {
    return `${this.T_SCOPE}.title`;
  }

  public get persistencyArea(): string {
    return this.pageCrud;
  }

  public get pageCrud(): string {
    return 'TelemetryPageCrud';
  }

  public get navigations(): DropdownNavigationItem[] {
    return [];
  }

  T_SCOPE = `telemetry.${COMPONENT_SELECTOR}`;

  gridSettings: GridSetting;
  gridSettingsReady$ = new ReplaySubject<GridSetting>();
  clearAll$ = new Subject<void>();
  canLoad: boolean = true;
  gridFiltersForBinding: DataBindingFilters;
  gridFilters: DataBindingFilters;
  selectedWorkspace: CtWorkspaceDto;
  selectedMeasurePoint: MeasurementDto;
  pointDescriptionPrefix: string;
  currentFilter: TelemetryFilters = null;
  additionalFilters: Map<string, string>;
  showSpinner = true;
  public readonly gridName = 'Measurements';
  private readonly _selectedWorkspaceService = inject(SelectedWorkspaceService);
  private readonly _destroyRef = inject(DestroyRef);

  constructor(private _gridService: GridSettingsService) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.init();

    this._selectedWorkspaceService.selectedWorkspace$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((workspace) => (this.selectedWorkspace = workspace));
  }

  mapInitParameters(parameters: TabDetailPanelParameters) { }

  init(): void {

    this.localization.get(`${this.T_SCOPE}.tab-settings`).subscribe((ts) => {
      const panelSettings = new TabDetailPanelSettings();
      panelSettings.addComponent(MeasurePointConfigurationComponent, ts['point-configuration']);

      this.pointDescriptionPrefix = ts['point-description-title'];

      this.rightPanelService.setTabSettings(panelSettings);
    });

    this._gridService.getGridSettingsByName(this.gridName).subscribe({
      next: (gridSettings) => {
        if (gridSettings) {
          this.gridSettings = gridSettings;
        }
      },
    });
  }

  loadGrid() {
    if (!this.currentFilter) {
      return;
    }

    const newGridFilters = new DataBindingFilters();
    let additionalFilters = new Map<string, string>();

    additionalFilters = new Map<string, string>(
      Object.entries(this.currentFilter).filter(([_, value]) => value !== ''),
    );

    this.additionalFilters = additionalFilters;
    this.gridFiltersForBinding = newGridFilters;
    this.showSpinner = false;
  }

  getSelectedMeasurePoint(selectedRow: MeasurementDto) {
    this.selectedMeasurePoint = selectedRow;
    this.sendParameters(selectedRow, null);
  }

  private sendParameters(selectedElement: MeasurementDto, filter: TabDetailPanelParameters) {
    const parameters = filter ?? new TabDetailPanelParameters();
    const title = selectedElement
      ? `${this.pointDescriptionPrefix}: ${selectedElement?.pointId}`
      : '';

    parameters.addParameter(TabDetailParameterName.signalId, selectedElement?.pointId);
    parameters.addParameter(TabDetailParameterName.modelId, this.currentFilter.modelId);
    parameters.addParameter(TabDetailParameterName.modelVersionId, this.currentFilter.modelVersionId);
    parameters.addParameter(TabDetailParameterName.elementName, title);

    this.rightPanelService.setTabParameters(parameters);
  }

  /**
   * Captures all the click events of all the buttons and associate them to the correct callbacks.
   */
  onClickGridBtns(event: GridBtnsEvent): void {
    switch (event.btn) {
      case GridBtnsOptions.ClearFilters:
        this.onClearAllFilters();
    }
  }

  onClearAllFilters(): void {
    this.clearAll$.next();
    this.sendParameters(null, null);
  }

  onApplyFilter(filter: TelemetryFilters) {
    this.currentFilter = filter;
    this.sendParameters(null, null);
    this.loadGrid();
  }
}
